import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, message } from 'antd';
import { MindMapContext } from '../../Context/contex';
import { debounce } from "lodash";
import useMindMapService from '../../hooks/useMindMapService';
import { emitNodeUpdate } from '../../Socket/socket';
import { useParams } from 'react-router-dom';
import { textStyleOf } from './textStyle';

const { TextArea } = Input;

const NodeInput = ({ data, inputStyle }) => {
  const { documentId } = useParams();
  const { isReadOnly, setNodes, setSavingData, selectedNodeId, setIsDocumentChanged, nodes, edges, recordChange } = MindMapContext();
  const [nodeName, setNodeName] = useState(data.label);
  // The map when editing starts: one undo step per editing session, focus to blur (D-036).
  const editStartRef = useRef(null);

  // Keep a stable ref to the latest nodeActions so the debounced callback
  // always has access to the current routing logic without being re-created.
  const mindMapService = useMindMapService();
  const mindMapServiceRef = useRef(mindMapService);
  useEffect(() => { mindMapServiceRef.current = mindMapService; }, [mindMapService]);

  useEffect(() => {
    setNodeName(data.label);
  }, [data.label]);

  const updateNodeName = useRef(debounce((selectedNodeId, payload, snapshot) => {
    mindMapServiceRef.current
      .updateNode(selectedNodeId, payload, snapshot)
      ?.then(() => setIsDocumentChanged(true))
      ?.catch((error) => {
        message.error('Error updating node');
        console.error('Error updating node:', error);
      });

    let emitPayload = {
      id: selectedNodeId,
      name: payload?.data?.label,
      documentId: documentId,
    };
    emitNodeUpdate(emitPayload);
  }, 400)).current;

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      updateNodeName.cancel();
    };
  }, [updateNodeName]);

  const handleChange = (event) => {
    // Marks the label as typed in: from now on Ctrl+Z undoes text here, not the map (UndoRedoButtons).
    event.target.dataset.edited = 'true';
    setSavingData({ isSaving: 'Saving...', lastSavedDateTime: '' });
    const newValue = event.target.value;
    let updatedNodeData = null;
    const updatedNodes = nodes.map((node) => {
      if (node.id === selectedNodeId) {
        updatedNodeData = {
          ...node.data,
          label: newValue,
        };
        return {
          ...node,
          data: updatedNodeData,
        };
      }
      return node;
    });
  
    if (updatedNodeData) {
      const payload = { data: updatedNodeData };
      setNodes(updatedNodes);
      updateNodeName(selectedNodeId, payload, { nodes: updatedNodes, edges });
    }

  };
  const handleTextAreaFocus = (event) => {
    event.target.setSelectionRange(0, 0);
    delete event.target.dataset.edited;
    editStartRef.current = { nodes, edges, nodeId: selectedNodeId, label: data.label };
  };
  const handleTextAreaBlur = () => {
    const start = editStartRef.current;
    editStartRef.current = null;
    if (!start || nodeName === start.label) return;
    const relabel = (node) => (node.id === start.nodeId ? { ...node, data: { ...node.data, label: nodeName } } : node);
    recordChange({ nodes: start.nodes, edges: start.edges }, { nodes: start.nodes.map(relabel), edges: start.edges });
  };

  return (
    <Form>
      <Form.Item>
        <TextArea
          placeholder="Enter text"
          value={nodeName}
          onChange={handleChange}
          onFocus={handleTextAreaFocus}
          onBlur={handleTextAreaBlur}
          data-map-label="true"
          autoSize={{ minRows: 2 }}
          readOnly={isReadOnly}
          style={{
            ...inputStyle,
            ...textStyleOf(data),
            padding: '0',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            color: data.textBg,
          }}
        />
      </Form.Item>
    </Form>
  );
};

export default NodeInput;
